import { useMemo, type ReactNode } from "react";

import "animate.css";
import clsx from "clsx";
import _ from "lodash";

import { GenericTooltip } from "@components/tooltip-container";
import { TertiaryButton } from "@shared/buttons";

export interface StepContainerProps {
  readonly children: ReactNode;
  readonly onMoveForwardButtonClicked?: () => void;
  readonly moveForwardButtonDisabled?: boolean;
  readonly moveForwardButtonTooltip?: React.ReactNode;
  readonly onMoveBackwardButtonClicked?: () => void;
  readonly title?: string;
  readonly isLast?: boolean;
  readonly hideFooter?: boolean;
  readonly onScrollStopped?: () => void;
  readonly knowledgeBaseLink: string;
  readonly knowledgeBaseText: string;
  readonly stepCompletionConfirmation?: ReactNode;
  readonly nextStepText?: string;
}

export const StepContainer = ({
  children,
  onMoveBackwardButtonClicked,
  onMoveForwardButtonClicked,
  moveForwardButtonDisabled,
  moveForwardButtonTooltip,
  onScrollStopped,
  title,
  knowledgeBaseLink,
  knowledgeBaseText,
  hideFooter,
  stepCompletionConfirmation,
  nextStepText,
}: StepContainerProps) => {
  const memoizedFunc = useMemo(
    () =>
      _.debounce(() => {
        onScrollStopped && onScrollStopped();
      }, 1000),
    [],
  );

  return (
    <div
      className={clsx(
        "bg-[#f4f7fe] flex flex-col h-full w-full items-center max-h-full",
      )}
    >
      <div
        onScroll={memoizedFunc}
        className={clsx(
          "grow w-full justify-center items-center overflow-y-auto px-6 pt-[20px]",
          "max-w-full overflow-x-hidden",
        )}
      >
        {title && (
          <div className="flex justify-between items-center py-3">
            <div className="py-4">
              <TertiaryButton
                onClick={() =>
                  onMoveBackwardButtonClicked && onMoveBackwardButtonClicked()
                }
                // eslint-disable-next-line max-len
                className="bg-cosmic-3 font-semibold py-2 px-4 rounded-full hover:bg-cosmic-2 flex flex-row items-center gap-2 hover:text-cosmic+1"
              >
                <i className="fa-sharp fa-regular fa-arrow-left fa-lg"></i>
                <div>BACK</div>
              </TertiaryButton>
            </div>
            <div className="flex items-center text-interstellar+0 text-4xl font-normal">
              {title}
            </div>
            <div className="py-4">
              <TertiaryButton
                onClick={() =>
                  onMoveForwardButtonClicked && onMoveForwardButtonClicked()
                }
                disabled={moveForwardButtonDisabled}
                // eslint-disable-next-line max-len
                className={clsx(
                  "bg-cosmic-3 font-semibold py-2 px-4 rounded-full flex flex-row items-center gap-2",
                  !moveForwardButtonDisabled &&
                    "hover:bg-cosmic-2 hover:text-cosmic+1",
                )}
              >
                <GenericTooltip
                  content={moveForwardButtonTooltip}
                  noInfoCircle
                  showTooltip={!!moveForwardButtonTooltip}
                >
                  <span>
                    {nextStepText || "NEXT"}
                    <i className="ml-2 fa-sharp fa-regular fa-arrow-right fa-lg"></i>
                  </span>
                </GenericTooltip>
              </TertiaryButton>
            </div>
          </div>
        )}
        {children}
        {!hideFooter && (
          <div
            className="min-h-[400px] mt-20 p-10 bg-white ml-[-24px] w-[calc(100%+48px)] border-t border-t-gray-0
        flex flex-col items-center font-semibold"
          >
            <div className="text-2xl font-normal">Need more guidance?</div>
            <div className="pb-10 flex gap-2 flex-col">
              <div className="text-sm font-semibold mt-10 uppercase">
                REFER TO OUR KNOWLEDGE BASE
              </div>
              <div className="flex gap-2 justify-center items-center">
                <i className="fa-light fa-newspaper text-cosmic"></i>
                <a
                  href={knowledgeBaseLink}
                  target="_blank"
                  className="font-normal text-xl"
                  rel="noreferrer"
                >
                  {knowledgeBaseText}
                </a>
              </div>
            </div>
            <div className="pb-10 flex items-center flex-col justify-center">
              <div className="text-sm font-semibold mt-10 uppercase pb-2">
                STILL NEED HELP?
              </div>
              <a
                className="font-normal text-xl"
                href="mailto:success@northbeam.io"
              >
                Contact us
              </a>
            </div>
          </div>
        )}
      </div>
      {!hideFooter && stepCompletionConfirmation && (
        <div className="sticky w-full z-10">{stepCompletionConfirmation}</div>
      )}
    </div>
  );
};
